import "./styles.css";
import { api, ApiError } from "./api/client";
import { store } from "./state";
import { ChatPanel } from "./ui/chat";
import { GraphCanvas } from "./ui/graph";
import { NodeDetailPanel } from "./ui/nodeDetail";
import { PipelineStrip } from "./ui/pipeline";
import { SuggestionsTray } from "./ui/suggestions";

const root = document.querySelector<HTMLElement>("#root")!;
root.innerHTML = `
  <div class="shell" data-view="chat">
    <header class="topbar">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">A</span>
        <div><strong>ARTI</strong><small>GRAFO COGNITIVO</small></div>
      </div>
      <div id="pipeline" class="pipeline-host"></div>
      <div class="topbar-actions">
        <button class="icon-button mobile-only" id="toggle-settings" aria-label="Ajustes" aria-expanded="false">⚙</button>
        <label class="field">
          <span>Usuário</span>
          <input id="user-id" spellcheck="false" aria-label="ID do usuário">
        </label>
        <a class="ghost-button" href="/lab" title="Painel de testes da API">API Lab</a>
        <span class="status" id="api-status"><i></i><span>verificando</span></span>
      </div>
    </header>

    <!--
      No celular nao cabem conversa e grafo lado a lado. Em vez de encolher os dois ate nenhum
      servir, a tela alterna entre eles — e abre na conversa, que e o que se usa a cada minuto.
      O grafo continua a um toque, e o alternador mostra quando ha algo novo nele.
    -->
    <nav class="view-switch mobile-only" role="tablist" aria-label="Alternar entre conversa e grafo">
      <button role="tab" data-view="chat" aria-selected="true">Conversa</button>
      <button role="tab" data-view="graph" aria-selected="false">
        Grafo <span class="view-badge" id="graph-badge" hidden></span>
      </button>
    </nav>

    <main class="workspace">
      <section class="panel chat-panel" id="chat"></section>
      <section class="panel graph-panel" id="graph"></section>
      <aside class="panel detail-panel" id="detail"></aside>
    </main>
  </div>
`;

const shell = root.querySelector<HTMLElement>(".shell")!;
const graphBadge = root.querySelector<HTMLElement>("#graph-badge")!;

/**
 * Alterna a coluna visivel no celular. No desktop as duas ficam lado a lado e o atributo nao
 * tem efeito — o mesmo DOM serve os dois layouts, sem componente duplicado.
 */
function showView(view: "chat" | "graph"): void {
  shell.dataset.view = view;
  for (const tab of root.querySelectorAll<HTMLButtonElement>(".view-switch button")) {
    tab.setAttribute("aria-selected", String(tab.dataset.view === view));
  }
  if (view === "graph") {
    graphBadge.hidden = true;
    graph.invalidateSize();
  }
}

for (const tab of root.querySelectorAll<HTMLButtonElement>(".view-switch button")) {
  tab.addEventListener("click", () => showView(tab.dataset.view === "graph" ? "graph" : "chat"));
}

root.querySelector<HTMLButtonElement>("#toggle-settings")!.addEventListener("click", (event) => {
  const button = event.currentTarget as HTMLButtonElement;
  const open = shell.classList.toggle("settings-open");
  button.setAttribute("aria-expanded", String(open));
});

const userInput = root.querySelector<HTMLInputElement>("#user-id")!;
userInput.value = store.state.userId;
userInput.addEventListener("change", () => {
  const value = userInput.value.trim();
  if (value.length === 0) {
    userInput.value = store.state.userId;
    return;
  }
  // Trocar de usuario troca de grafo e de conversa: manter qualquer um dos dois misturaria
  // dois estados cognitivos distintos na mesma tela.
  store.update({
    userId: value,
    conversationId: null,
    messages: [],
    lastResponse: null,
    selectedNodeKey: null,
    focusKey: null,
    suggestions: []
  });
  void loadGraph();
  void suggestions.load();
});

new PipelineStrip(root.querySelector<HTMLElement>("#pipeline")!);

new ChatPanel(
  root.querySelector<HTMLElement>("#chat")!,
  () => {
    // Um turno concluido pode ter aprovado conhecimento novo; o grafo e relido para refletir.
    void loadGraph();
  },
  // A extracao terminou e produziu sugestoes: a bandeja recarrega sozinha, sem o usuario
  // precisar descobrir que ha algo a aprovar.
  () => void suggestions.load()
);

// A bandeja vive dentro do painel de conversa, cujo HTML foi montado logo acima: a sugestao
// aparece abaixo da mensagem que a originou, e aprovar faz o no nascer no grafo ao lado sem
// trocar de tela.
const suggestions = new SuggestionsTray(
  root.querySelector<HTMLElement>("#suggestions-tray")!,
  () => void loadGraph()
);

const graph = new GraphCanvas(
  root.querySelector<HTMLElement>("#graph")!,
  (key) => store.update({ selectedNodeKey: key }),
  (key) => {
    store.update({ focusKey: key, selectedNodeKey: key });
    void loadGraph();
  }
);

new NodeDetailPanel(
  root.querySelector<HTMLElement>("#detail")!,
  (key) => {
    store.update({ focusKey: key });
    void loadGraph();
  },
  () => store.update({ selectedNodeKey: null })
);

root.querySelector<HTMLElement>("#detail")!.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const key = target.dataset.key;
  if (key) {
    store.update({ selectedNodeKey: key });
  }
});

async function loadGraph(): Promise<void> {
  const { userId, focusKey, depth } = store.state;
  const previousUpdate = store.state.graph?.lastUpdated ?? null;
  store.update({ graphLoading: true, graphError: null });
  try {
    const graph = await api.getCognitiveGraph({ userId, focus: focusKey, depth });
    store.update({ graph, graphLoading: false });
    // No celular o grafo fica atras de uma aba. Sem sinal, aprovar uma sugestao pareceria nao
    // ter efeito nenhum — o no nasce numa tela que o usuario nao esta vendo. O `lastUpdated`
    // do envelope e o que permite distinguir grafo novo de simples recarga.
    if (previousUpdate !== null && graph.lastUpdated !== previousUpdate && shell.dataset.view !== "graph") {
      graphBadge.hidden = false;
    }
  } catch (error) {
    store.update({
      graphLoading: false,
      graphError: error instanceof ApiError ? error.message : "Não foi possível carregar o grafo."
    });
  }
}

async function refreshHealth(): Promise<void> {
  const online = await api.isHealthy();
  store.update({ apiOnline: online });
  const status = root.querySelector<HTMLElement>("#api-status")!;
  status.classList.toggle("offline", !online);
  status.querySelector("span")!.textContent = online ? "online" : "offline";
}

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    if (store.state.selectedNodeKey) {
      store.update({ selectedNodeKey: null });
    } else if (store.state.focusKey) {
      store.update({ focusKey: null });
      void loadGraph();
    }
  }
});

void refreshHealth();
void loadGraph();
void suggestions.load();
setInterval(() => void refreshHealth(), 30_000);
